import { PropertyLookupDetail } from '../../shared/models/property-lookup-detail';
import { FormGroup } from '@angular/forms';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { Property } from '../models/property';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { Vendor } from '../models/vendor';
import { TaxRequest } from '../models/TaxRequest';
import { TaxEntity } from '../models/TaxEntity';
import { List } from 'immutable';
import { TaxResponse } from '../models/TaxResponse';

export class PropertyLookupConverter {

  static updateFormFields(detail: PropertyLookupDetail,
                          contactLookupForm: FormGroup,
                          propertyLookupStore: PropertyLookupStore) {

    contactLookupForm.patchValue({
      name: detail.name,
      line1: detail.address.line1,
      line2: detail.address.line2,
      city: detail.address.city,
      state: detail.address.state,
      postalCode: detail.address.postalCode,
      propertyId: detail.propertyId,
      chain: detail.chain,
      brand: detail.brand,
      countryCode: detail.address.countryCode,
      manuallyAdded: detail.manuallyAdded
    });
    contactLookupForm.controls[ 'contact' ].patchValue({
      firstName: detail.contact.firstName,
      lastName: detail.contact.lastName,
      email: detail.contact.email,
      phone: detail.contact.phone,
      fax: detail.contact.fax
    });
    contactLookupForm.markAsDirty();


    contactLookupForm.controls[ 'propertyNameSearch' ].setValue('');
    propertyLookupStore.clearSearchResults();
  }

  /**
   * Convert a Property into Party
   *
   * @param property Property object
   * @param autoGeneratedCode boolean
   * @param manuallyAdded boolean
   * @returns {Party}
   */
  static propertyToParty(property: Property, autoGeneratedCode: boolean): Party {
    return new Party({
      eventPartyId: null,
      version: null,
      createdDate: null,
      modifiedDate: null,
      chain: property.chain,
      brand: property.brand,
      propertyId: property.pid,
      agencyId: null,
      partyType: PartyType.PROPERTY,
      payorType: property.payorType,
      code: null,
      name: property.name,
      dbaName: null,
      countryCode: property.countryCode,
      eventId: null,
      line1: property.address1,
      line2: property.address2,
      line3: property.address3,
      city: property.city,
      state: property.stateProvinceCode,
      postalCode: property.postalCode,
      eventReferenceNumber: null,
      autoGeneratedCode: autoGeneratedCode,
      manuallyAdded: property.manuallyAdded,
      contact: {
        eventContactId: null,
        version: null,
        createdDate: null,
        modifiedDate: null,
        partyType: PartyType.PROPERTY,
        eventId: null,
        firstName: property.contactFirstNm,
        lastName: property.contactLastNm,
        email: property.email,
        phone: property.phoneNr,
        fax: property.faxNr
      },
      subscriber: property.subscriber
    });
  }

  static createTaxRequest(vendor: Vendor, eventAgency: any): TaxRequest {
    let buyer: TaxEntity = new TaxEntity({
      countryCode: vendor.countryCode,
      region: vendor.stateProvinceCode,
      subRegion: null,
      postalCode: null,
      city: vendor.city
    });

    let supplier: TaxEntity = new TaxEntity({
      countryCode: eventAgency.countryCode,
      region: eventAgency.state,
      subRegion: null,
      postalCode: null,
      city: eventAgency.city
    });

    return new TaxRequest({
      supplyDate: null,
      buyer: buyer,
      supplier: supplier,
      billedTo: buyer
    });
  }

  static vendorToParty(vendor: Vendor, autoGeneratedCode: boolean, taxResponseList: List<TaxResponse>): Party {
    return new Party({
      eventPartyId: null,
      version: null,
      createdDate: null,
      modifiedDate: null,
      chain: vendor.chain,
      brand: vendor.brand,
      propertyId: vendor.pid,
      agencyId: null,
      partyType: PartyType.PROPERTY,
      payorType: vendor.payorType,
      code: null,
      name: vendor.name,
      dbaName: null,
      countryCode: vendor.countryCode,
      eventId: null,
      line1: vendor.address1,
      line2: vendor.address2,
      line3: vendor.address3,
      city: vendor.city,
      state: vendor.stateProvinceCode,
      postalCode: vendor.postalCode,
      eventReferenceNumber: null,
      autoGeneratedCode: autoGeneratedCode,
      manuallyAdded: vendor.manuallyAdded,
      commissionRoomPercentage: vendor.commissionRoomPercentage,
      commRetention: vendor.commRetention,
      contact: {
        eventContactId: null,
        version: null,
        createdDate: null,
        modifiedDate: null,
        partyType: PartyType.PROPERTY,
        eventId: null,
        firstName: vendor.contactFirstNm,
        lastName: vendor.contactLastNm,
        email: vendor.email,
        phone: vendor.phoneNr,
        fax: vendor.faxNr
      },
      subscriber: vendor.subscriber,
      taxResponseList: taxResponseList
    });
  }

  static convertPropertyLookupDetailToProperty(propertyLookupDetail: PropertyLookupDetail): Property {
    return new Property({
      chain: propertyLookupDetail.chain,
      brand: propertyLookupDetail.brand,
      pid: propertyLookupDetail.propertyId,
      name: propertyLookupDetail.name,
      address1: propertyLookupDetail.address ? propertyLookupDetail.address.line1 : null,
      address2: propertyLookupDetail.address ? propertyLookupDetail.address.line2 : null,
      address3: propertyLookupDetail.address ? propertyLookupDetail.address.line3 : null,
      city: propertyLookupDetail.address ? propertyLookupDetail.address.city : null,
      stateProvinceCode: propertyLookupDetail.address ? propertyLookupDetail.address.state : null,
      countryCode: propertyLookupDetail.address ? propertyLookupDetail.address.countryCode : null,
      postalCode: propertyLookupDetail.address ? propertyLookupDetail.address.postalCode : null,
      phoneNr: propertyLookupDetail.contact ? propertyLookupDetail.contact.phone : null,
      faxNr: propertyLookupDetail.contact ? propertyLookupDetail.contact.fax : null,
      email: propertyLookupDetail.contact ? propertyLookupDetail.contact.email : null,
      contactFirstNm: propertyLookupDetail.contact ? propertyLookupDetail.contact.firstName : null,
      contactLastNm: propertyLookupDetail.contact ? propertyLookupDetail.contact.lastName : null,
      subscriber: propertyLookupDetail.subscriber,
      manuallyAdded: propertyLookupDetail.manuallyAdded,
      payorType: propertyLookupDetail.payorType
    });
  }

  static propertyToVendor(property: Property, vendor: Vendor): Vendor {
    return new Vendor({
      id: property.id,
      chain: property.chain,
      brand: property.brand,
      pid: property.pid,
      name: property.name,
      address1: property.address1,
      address2: property.address2,
      address3: property.address3,
      city: property.city,
      stateProvinceCode: property.stateProvinceCode,
      countryCode: property.countryCode,
      postalCode: property.postalCode,
      phoneNr: property.phoneNr,
      faxNr: property.faxNr,
      email: property.email,
      contactFirstNm: property.contactFirstNm,
      contactLastNm: property.contactLastNm,
      subscriber: property.subscriber,
      manuallyAdded: property.manuallyAdded,
      payorType: property.payorType,
      commissionRoomPercentage: vendor.commissionRoomPercentage,
      commRetention: vendor.commRetention
    });
  }

  static vendorToProperty(vendor: Vendor): Property {
    return new Property({
      id: vendor.id,
      chain: vendor.chain,
      brand: vendor.brand,
      pid: vendor.pid,
      name: vendor.name,
      address1: vendor.address1,
      address2: vendor.address2,
      address3: vendor.address3,
      city: vendor.city,
      stateProvinceCode: vendor.stateProvinceCode,
      countryCode: vendor.countryCode,
      postalCode: vendor.postalCode,
      phoneNr: vendor.phoneNr,
      faxNr: vendor.faxNr,
      email: vendor.email,
      contactFirstNm: vendor.contactFirstNm,
      contactLastNm: vendor.contactLastNm,
      subscriber: vendor.subscriber,
      manuallyAdded: vendor.manuallyAdded,
      payorType: vendor.payorType
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/utils/property-lookup-converter.ts